import { useEffect, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  PUBLIC_SURVEY_LANG_KEY,
  isPublicSurveyLanguage,
  normalizePublicSurveyLanguage,
  publicSurveyI18n,
  syncDocumentLanguage,
} from './i18n';

interface PublicSurveyI18nProviderProps {
  children: ReactNode;
}

/** Provider i18n isolado para o fluxo público de pesquisas. */
export function PublicSurveyI18nProvider({ children }: PublicSurveyI18nProviderProps) {
  const [searchParams] = useSearchParams();
  const langParam = searchParams.get('lang');

  useEffect(() => {
    const fromUrl = langParam ? normalizePublicSurveyLanguage(langParam) : null;
    const fromStorage = localStorage.getItem(PUBLIC_SURVEY_LANG_KEY);
    const resolved = fromUrl ?? (fromStorage && isPublicSurveyLanguage(fromStorage) ? fromStorage : null);

    if (resolved && resolved !== publicSurveyI18n.language) {
      void publicSurveyI18n.changeLanguage(resolved);
    }

    syncDocumentLanguage(publicSurveyI18n.language);

    const onLanguageChanged = (lng: string) => {
      syncDocumentLanguage(lng);
      if (isPublicSurveyLanguage(lng)) {
        localStorage.setItem(PUBLIC_SURVEY_LANG_KEY, lng);
      }
    };

    publicSurveyI18n.on('languageChanged', onLanguageChanged);
    return () => {
      publicSurveyI18n.off('languageChanged', onLanguageChanged);
    };
  }, [langParam]);

  return <I18nextProvider i18n={publicSurveyI18n}>{children}</I18nextProvider>;
}
